/**
 * MODIFIED: 2025-01-29 - refactor/task-1.5-dashboard-performance
 *
 * ARCHITECTURE: Service Architecture
 * PURPOSE: Type-safe service registry with Symbol-based keys for compile-time safety and autocomplete
 *
 * NEW PATTERNS IMPLEMENTED:
 * - BaseService Pattern: Lifecycle management (IDLE → INITIALIZING → INITIALIZED/FAILED)
 * - Dependency Graph: Topological sort for initialization ordering
 * - Type-Safe Registry: Symbol-based service keys with phantom types
 * - Parallel Initialization: Concurrent service startup (~700ms savings)
 *
 * KEY FEATURES:
 * - Symbol-based keys prevent string typos at compile time
 * - Phantom type parameter for automatic type inference
 * - SERVICE_KEYS enum-style constants for autocomplete
 * - ServiceDefinition metadata (dependencies, critical, lazy, priority)
 * - Centralized service registration and lookup
 *
 * RELATED FILES:
 * - src/lib/services/core/BaseService.ts
 * - src/lib/services/core/ServiceManager.ts
 * - src/lib/services/core/dependency-graph.ts
 */

// services/core/registry.ts
/**
 * Type-Safe Service Registry
 *
 * Symbol-based service key system for compile-time type safety and autocomplete.
 * Replaces string-based service lookup with proper TypeScript inference.
 *
 * @example
 * ```typescript
 * // Old way (error-prone):
 * const alerts = serviceManager.getService<AlertsService>('alertsService');
 *
 * // New way (type-safe):
 * const alerts = serviceRegistry.get(SERVICE_KEYS.ALERTS);
 * ```
 */

// ============================================================================
// TYPE-SAFE SERVICE KEY SYSTEM
// ============================================================================

/**
 * Type-safe service key with phantom type parameter
 * The __type property is never actually used at runtime, it only exists for TypeScript
 */
export type ServiceKey<T> = symbol & { __type?: T };

/**
 * Service Keys - ENUM-style constant for autocomplete and type safety
 *
 * CRITICAL: Add ALL services here! Each key must:
 * 1. Use Symbol('name') for uniqueness
 * 2. Cast to ServiceKey<T> with correct type
 * 3. Use dynamic import type for the service
 */
export const SERVICE_KEYS = {
	// ========================================================================
	// CORE SERVICES
	// ========================================================================

	/**
	 * Application initialization service
	 * Pattern: Functional (runes-based)
	 */
	APP: Symbol('app') as ServiceKey<typeof import('../app/appInitializer').appService>,

	/**
	 * Authentication service
	 * Pattern: BaseService (OOP)
	 */
	AUTH: Symbol('auth') as ServiceKey<typeof import('../auth/AuthService').authService>,

	/**
	 * WebSocket real-time communication service
	 * Pattern: Functional (runes-based)
	 */
	WEBSOCKET: Symbol('websocket') as ServiceKey<
		typeof import('../websocket/WebSocketService').websocketService
	>,

	// ========================================================================
	// DATA SERVICES
	// ========================================================================

	/**
	 * Alerts service
	 * Pattern: BaseService (OOP)
	 */
	ALERTS: Symbol('alerts') as ServiceKey<typeof import('../business/AlertsService').alertsService>,

	/**
	 * Work orders service
	 * Pattern: BaseService (OOP)
	 */
	WORK_ORDERS: Symbol('workOrders') as ServiceKey<
		typeof import('../business/WorkOrderService').workOrderService
	>,

	/**
	 * Audit log service
	 * Pattern: Functional wrapper
	 */
	AUDIT_LOG: Symbol('auditLog') as ServiceKey<typeof import('../business/index').auditLogService>,

	// ========================================================================
	// PRESENCE & NOTIFICATIONS
	// ========================================================================

	/**
	 * Unified presence service (user online status)
	 * Pattern: BaseService (OOP)
	 */
	UNIFIED_PRESENCE: Symbol('unifiedPresence') as ServiceKey<
		typeof import('../presence/UnifiedPresenceService').unifiedPresenceService
	>,

	/**
	 * Notification service (toasts, notifications)
	 * Pattern: Functional (runes-based)
	 */
	NOTIFICATION: Symbol('notification') as ServiceKey<
		typeof import('../notifications/NotificationService').notificationService
	>,

	// ========================================================================
	// ORCHESTRATION
	// ========================================================================

	/**
	 * Unified data update orchestrator
	 * Pattern: Functional (runes-based, .svelte.ts)
	 */
	DATA_UPDATE_ORCHESTRATOR: Symbol('dataUpdateOrchestrator') as ServiceKey<
		typeof import('../orchestration/UnifiedDataUpdateOrchestrator.svelte').unifiedDataUpdateOrchestrator
	>,

	// ========================================================================
	// ADMIN SERVICES
	// ========================================================================

	/**
	 * Organizations service
	 * Pattern: BaseService (OOP wrapper)
	 */
	ORGANIZATIONS: Symbol('organizations') as ServiceKey<
		typeof import('../admin/OrganizationsService').organizationsService
	>,

	/**
	 * Departments service
	 * Pattern: BaseService (OOP wrapper)
	 */
	DEPARTMENTS: Symbol('departments') as ServiceKey<
		typeof import('../admin/DepartmentsService').departmentsService
	>,

	/**
	 * Roles service
	 * Pattern: BaseService (OOP wrapper)
	 */
	ROLES: Symbol('roles') as ServiceKey<typeof import('../admin/RolesService').rolesService>,

	/**
	 * Permissions service
	 * Pattern: BaseService (OOP wrapper)
	 */
	PERMISSIONS: Symbol('permissions') as ServiceKey<
		typeof import('../admin/PermissionsService').permissionsService
	>,

	/**
	 * Pages service
	 * Pattern: BaseService (OOP wrapper)
	 */
	PAGES: Symbol('pages') as ServiceKey<typeof import('../admin/PagesService').pagesService>,

	/**
	 * Admin dashboard service
	 * Pattern: BaseService (OOP wrapper)
	 */
	ADMIN_DASHBOARD: Symbol('adminDashboard') as ServiceKey<
		typeof import('../admin/AdminDashboardService').adminDashboardService
	>,

	/**
	 * OPA policy service
	 * Pattern: BaseService (OOP wrapper)
	 */
	OPA: Symbol('opa') as ServiceKey<typeof import('../admin/OpaService').opaService>,

	// ========================================================================
	// UTILITY SERVICES
	// ========================================================================

	/**
	 * Offline service (PWA functionality)
	 * Pattern: Class-based
	 */
	OFFLINE: Symbol('offline') as ServiceKey<typeof import('../offline.service').offlineService>
} as const;

// ============================================================================
// SERVICE DEFINITION METADATA
// ============================================================================

/**
 * Service definition with dependency graph and initialization metadata
 */
export interface ServiceDefinition {
	/** Unique symbol key for this service */
	key: symbol;

	/**
	 * Dependencies required before this service can initialize
	 * Use SERVICE_KEYS symbols here, not strings!
	 */
	dependencies: symbol[];

	/**
	 * If true, app won't start if this service fails to initialize
	 * Examples: AUTH, WEBSOCKET
	 */
	critical: boolean;

	/**
	 * If true, initialize on first use instead of app startup
	 * Examples: AUDIT_LOG, OFFLINE
	 */
	lazy: boolean;

	/**
	 * Initialization priority within the same dependency level
	 * Lower number = higher priority (0 = highest)
	 */
	priority: number;
}

// ============================================================================
// TYPE-SAFE SERVICE REGISTRY
// ============================================================================

/**
 * Central service registry with type-safe Symbol-based lookup
 *
 * Features:
 * - Compile-time type safety
 * - Autocomplete for all service keys
 * - No string typos possible
 * - Dependency metadata for initialization ordering
 */
export class ServiceRegistry {
	private services = new Map<symbol, any>();
	private definitions = new Map<symbol, ServiceDefinition>();

	/**
	 * Register a service with its metadata
	 *
	 * @example
	 * ```typescript
	 * serviceRegistry.register(SERVICE_KEYS.AUTH, authService, {
	 *   dependencies: [SERVICE_KEYS.APP],
	 *   critical: true,
	 *   lazy: false,
	 *   priority: 0
	 * });
	 * ```
	 */
	register<T>(key: ServiceKey<T>, service: T, definition: Omit<ServiceDefinition, 'key'>): void {
		this.services.set(key as symbol, service);
		this.definitions.set(key as symbol, {
			key: key as symbol,
			...definition
		});
	}

	/**
	 * Get a service (type-safe)
	 *
	 * @example
	 * ```typescript
	 * const alerts = serviceRegistry.get(SERVICE_KEYS.ALERTS);
	 * // TypeScript knows alerts is AlertsService!
	 * ```
	 *
	 * @throws {Error} If service not found
	 */
	get<T>(key: ServiceKey<T>): T {
		const service = this.services.get(key as symbol);
		if (!service) {
			throw new Error(`Service not found: ${String(key)}`);
		}
		return service as T;
	}

	/**
	 * Check if service exists
	 */
	has(key: symbol): boolean {
		return this.services.has(key);
	}

	/**
	 * Get service definition metadata
	 */
	getDefinition(key: symbol): ServiceDefinition | undefined {
		return this.definitions.get(key);
	}

	/**
	 * Get all registered service keys
	 */
	getAllKeys(): symbol[] {
		return Array.from(this.services.keys());
	}

	/**
	 * Get all service definitions
	 */
	getAllDefinitions(): ServiceDefinition[] {
		return Array.from(this.definitions.values());
	}

	/**
	 * Unregister a service (mainly for testing)
	 */
	unregister(key: symbol): void {
		this.services.delete(key);
		this.definitions.delete(key);
	}

	/**
	 * Clear all services (mainly for testing)
	 */
	clear(): void {
		this.services.clear();
		this.definitions.clear();
	}
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

/**
 * Global service registry instance
 * Use this throughout the application for service access
 */
export const serviceRegistry = new ServiceRegistry();
